// Adaptive display formatters for the Interest Modeling carry portfolio.
// Mirrors the PM mock's `fmtUsd` / `fmtMonth` (the interaction truth): package royalty
// values run in the thousands while a full working interest runs in the millions, so
// one fixed unit can't read cleanly across the range — the currency label picks the
// largest fitting unit. The numeric part goes through `formatNumber` (repo convention,
// consistent rounding); this only adds the `$` + lowercase `k/mm/b` suffix the spec
// locks (the shared `formatScaledNumber` renders uppercase `K/MM/B`, Highcharts-scoped).
import { formatNumber } from 'utils/number';

/**
 * Adaptive USD: `$850k` / `$1.2mm` / `$1.1b`, else whole dollars. Sign is kept
 * (a drawdown reads `$-1.2mm`); callers color by sign. Non-finite ⇒ em dash.
 *
 * @param {number} value - The dollar amount.
 * @return {string} The abbreviated currency label.
 */
export function fmtUsd(value: number): string {
  if (!Number.isFinite(value)) {
    return '—';
  }
  const magnitude = Math.abs(value);
  if (magnitude >= 1e9) {
    return `$${formatNumber({
      value: value / 1e9,
      minPrecision: 1,
      maxPrecision: 1,
    })}b`;
  }
  if (magnitude >= 1e6) {
    return `$${formatNumber({
      value: value / 1e6,
      minPrecision: 1,
      maxPrecision: 1,
    })}mm`;
  }
  if (magnitude >= 1e3) {
    return `$${formatNumber({
      value: value / 1e3,
      minPrecision: 0,
      maxPrecision: 0,
    })}k`;
  }
  return `$${formatNumber({ value, minPrecision: 0, maxPrecision: 0 })}`;
}

/**
 * A payout / breakeven month as `Mo 34 · 2.8yr`; `null` (never happens) ⇒ em dash.
 *
 * @param {number | null} month - The 1-based project month, or null.
 * @return {string} The month · years label.
 */
export function fmtMonth(month: number | null): string {
  if (month === null || !Number.isFinite(month)) {
    return '—';
  }
  const years = formatNumber({
    value: month / 12,
    minPrecision: 1,
    maxPrecision: 1,
  });
  return `Mo ${month} · ${years}yr`;
}
